import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Upload, Tooltip, Checkbox, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined, DownloadOutlined, CopyOutlined, DownOutlined, SettingOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useCaseApi, businessScenarioApi, annotationApi, judgeSetApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { Search, TextArea } = Input;
const { Option } = Select;

function UseCaseList() {
  const navigate = useNavigate();
  const { currentScenario, currentDomain, hasManagePermission } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [businessScenarios, setBusinessScenarios] = useState([]);
  const [judgeSets, setJudgeSets] = useState([]);
  const [filteredJudgeSets, setFilteredJudgeSets] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    caseName: '',
    scenarioId: undefined,
    source: undefined,
    attachmentType: undefined,
    tags: undefined,
  });

  // 选中的行
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();

  // 批量上传状态管理
  const [uploadProgress, setUploadProgress] = useState({
    uploading: false,
    current: 0,
    total: 0,
    percent: 0
  });

  // 防止重复上传的标记
  const [isUploading, setIsUploading] = useState(false);
  // 当前选择的文件列表
  const [selectedFiles, setSelectedFiles] = useState([]);
  // 附件上传状态管理
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  // 标注结果导入相关状态
  const [annotationModalVisible, setAnnotationModalVisible] = useState(false);
  const [annotationForm] = Form.useForm();
  const [annotationFile, setAnnotationFile] = useState(null);
  const [annotationUploading, setAnnotationUploading] = useState(false);

  // Excel导入用例相关状态（要素抽取场景专用）
  const [excelImportModalVisible, setExcelImportModalVisible] = useState(false);
  const [excelImportForm] = Form.useForm();
  const [excelImportFile, setExcelImportFile] = useState(null);
  const [excelImportUploading, setExcelImportUploading] = useState(false);

  // 机审场景批量导入相关状态
  const [autoReviewImportModalVisible, setAutoReviewImportModalVisible] = useState(false);
  const [autoReviewImportFile, setAutoReviewImportFile] = useState(null);
  const [autoReviewImportUploading, setAutoReviewImportUploading] = useState(false);
  const [autoReviewImportPreview, setAutoReviewImportPreview] = useState(null);

  // 配置业务场景相关状态
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configType, setConfigType] = useState(''); // 'visible' 或 'editable'
  const [configForm] = Form.useForm();
  const [configLoading, setConfigLoading] = useState(false);
  const [currentConfigRecord, setCurrentConfigRecord] = useState(null);
  const [isBatchConfig, setIsBatchConfig] = useState(false);
  // 项目空间下的业务场景列表（用于配置弹窗）
  const [domainScenarios, setDomainScenarios] = useState([]);
  const [domainScenariosLoading, setDomainScenariosLoading] = useState(false);

  // 批量添加评测集相关状态
  const [batchBindJudgeSetsModalVisible, setBatchBindJudgeSetsModalVisible] = useState(false);
  const [batchBindJudgeSetsForm] = Form.useForm();
  const [batchBindJudgeSetsLoading, setBatchBindJudgeSetsLoading] = useState(false);
  // 当前业务场景下的评测集列表
  const [scenarioJudgeSets, setScenarioJudgeSets] = useState([]);

  // 批量删除相关状态
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // 用例来源
  const sources = ['线上', '手工'];

  // 文件类型选项（动态从接口获取）
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [fileTypeLoading, setFileTypeLoading] = useState(false);

  useEffect(() => {
    fetchBusinessScenarios();
    fetchJudgeSets();
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);

  // 页面加载时获取项目空间下的业务场景列表（用于顶部筛选）
  useEffect(() => {
    if (currentDomain?.id) {
      fetchDomainScenarios();
    }
  }, [currentDomain?.id]);

  // 当业务场景变化时获取文件类型选项（要素抽取和机审场景）
  useEffect(() => {
    if (currentScenario?.id && (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW')) {
      fetchFileTypes();
    } else {
      setFileTypeOptions([]);
    }
  }, [currentScenario?.id, currentScenario?.scenarioCategory]);

  // 获取文件类型选项
  const fetchFileTypes = async () => {
    if (!currentScenario?.id) return;
    
    setFileTypeLoading(true);
    try {
      const response = await useCaseApi.getFileTypesByScenario(currentScenario.id);
      setFileTypeOptions(response || []);
    } catch (error) {
      console.error('获取文件类型列表失败:', error);
      setFileTypeOptions([]);
    } finally {
      setFileTypeLoading(false);
    }
  };

  const fetchBusinessScenarios = async () => {
    try {
      const response = await businessScenarioApi.getList({ pageSize: 1000 });
      setBusinessScenarios(response.list || []);
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
    }
  };

  const fetchJudgeSets = async () => {
    try {
      const response = await judgeSetApi.getList({ pageSize: 1000 });
      setJudgeSets(response.list || []);
      setFilteredJudgeSets(response.list || []);
    } catch (error) {
      console.error('获取评测集列表失败:', error);
    }
  };

  const filterJudgeSetsByScenario = (scenarioId) => {
    if (!scenarioId) {
      setFilteredJudgeSets(judgeSets);
    } else {
      const filtered = judgeSets.filter(set => set.businessScenarioId === scenarioId);
      setFilteredJudgeSets(filtered);
    }
  };

  const fetchData = async () => {
    if (!currentScenario) return;

    setLoading(true);
    try {
      // 判断选择的业务场景和当前进入的业务场景是否一致
      const selectedScenarioId = searchParams.scenarioId || currentScenario.id;
      const overScenario = searchParams.scenarioId && searchParams.scenarioId !== currentScenario.id;

      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        caseName: searchParams.caseName || undefined,
        scenarioId: selectedScenarioId, // 优先使用筛选的业务场景ID，否则使用当前业务场景的ID
        source: searchParams.source || undefined,
        // 仅要素抽取场景传递文件类型和标签参数
        attachmentType: (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? (searchParams.attachmentType || undefined) : undefined,
        tags: (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? (searchParams.tags || undefined) : undefined,
        overScenario: overScenario, // 选择的业务场景和当前进入的业务场景不一致时为true，一致时为false
        currentScenarioId:currentScenario.id
      };

      const response = await useCaseApi.getList(params);
      
      setDataSource(response.list || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));
      // 清空选中状态
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('获取用例列表失败:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, caseName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleScenarioFilter = (scenarioId) => {
    setSearchParams(prev => ({ ...prev, scenarioId }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSourceFilter = (source) => {
    setSearchParams(prev => ({ ...prev, source }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFileTypeFilter = (attachmentType) => {
    setSearchParams(prev => ({ ...prev, attachmentType }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTagFilter = (tags) => {
    setSearchParams(prev => ({ ...prev, tags }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 加载项目空间下的业务场景列表
  const fetchDomainScenarios = async () => {
    if (!currentDomain?.id) {
      message.warning('请先进入项目空间');
      return;
    }

    setDomainScenariosLoading(true);
    try {
      const scenarios = await businessScenarioApi.getListByDomain(currentDomain.id);
      setDomainScenarios(scenarios || []);
    } catch (error) {
      console.error('获取项目空间业务场景列表失败:', error);
      setDomainScenarios([]);
    } finally {
      setDomainScenariosLoading(false);
    }
  };

  // 批量添加评测集
  const handleBatchBindJudgeSets = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要添加评测集的用例');
      return;
    }
    // 获取当前业务场景下的评测集
    const currentScenarioJudgeSets = judgeSets.filter(set => set.businessScenarioId === currentScenario?.id);
    setScenarioJudgeSets(currentScenarioJudgeSets);
    setBatchBindJudgeSetsModalVisible(true);
    batchBindJudgeSetsForm.resetFields();
  };

  // 批量添加评测集确认
  const handleBatchBindJudgeSetsOk = async () => {
    try {
      const values = await batchBindJudgeSetsForm.validateFields();

      if (!values.setIds || values.setIds.length === 0) {
        message.error('请至少选择一个评测集');
        return;
      }

      setBatchBindJudgeSetsLoading(true);

      const data = {
        caseIds: selectedRowKeys,
        setIds: values.setIds,
      };

      await useCaseApi.batchBindJudgeSets(data);

      message.success(`成功为 ${selectedRowKeys.length} 个用例添加 ${values.setIds.length} 个评测集`);
      setBatchBindJudgeSetsModalVisible(false);
      batchBindJudgeSetsForm.resetFields();
      fetchData();
    } catch (error) {
      console.error('批量添加评测集失败:', error);
    } finally {
      setBatchBindJudgeSetsLoading(false);
    }
  };

  // 批量删除用例
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的用例');
      return;
    }

    // 校验批量选择的用例中是否包含不属于当前业务场景的用例
    const selectedRecords = dataSource.filter(record => selectedRowKeys.includes(record.id));
    const hasNonCurrentScenarioCase = selectedRecords.some(record => record.scenarioId !== currentScenario?.id);
    if (hasNonCurrentScenarioCase) {
      message.warning('批量选择的用例中包含归属其他业务场景的用例，无法进行批量删除操作');
      return;
    }

    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个用例吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setBatchDeleteLoading(true);
          await useCaseApi.batchDelete({ caseIds: selectedRowKeys });
          message.success(`成功删除 ${selectedRowKeys.length} 个用例`);
          setSelectedRowKeys([]);
          fetchData();
        } catch (error) {
          console.error('批量删除用例失败:', error);
        } finally {
          setBatchDeleteLoading(false);
        }
      },
    });
  };

  // 批量配置可见业务场景
  const handleBatchConfigVisible = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要配置的用例');
      return;
    }
    // 校验批量选择的用例中是否包含不属于当前业务场景的用例
    const selectedRecords = dataSource.filter(record => selectedRowKeys.includes(record.id));
    const hasNonCurrentScenarioCase = selectedRecords.some(record => record.scenarioId !== currentScenario?.id);
    if (hasNonCurrentScenarioCase) {
      message.warning('批量选择的用例中包含归属其他业务场景的用例，无法进行批量配置可见业务场景操作');
      return;
    }
    setConfigType('visible');
    setIsBatchConfig(true);
    setCurrentConfigRecord(null);
    setConfigModalVisible(true);
    configForm.resetFields();
    // 加载项目空间下的业务场景列表
    await fetchDomainScenarios();
  };

  // 批量配置可修改业务场景
  const handleBatchConfigEditable = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要配置的用例');
      return;
    }
    // 校验批量选择的用例中是否包含不属于当前业务场景的用例
    const selectedRecords = dataSource.filter(record => selectedRowKeys.includes(record.id));
    const hasNonCurrentScenarioCase = selectedRecords.some(record => record.scenarioId !== currentScenario?.id);
    if (hasNonCurrentScenarioCase) {
      message.warning('批量选择的用例中包含归属其他业务场景的用例，无法进行批量配置可修改业务场景操作');
      return;
    }
    setConfigType('editable');
    setIsBatchConfig(true);
    setCurrentConfigRecord(null);
    setConfigModalVisible(true);
    configForm.resetFields();
    // 加载项目空间下的业务场景列表
    await fetchDomainScenarios();
  };

  // 配置可见业务场景
  const handleConfigVisible = async (record) => {
    setConfigType('visible');
    setIsBatchConfig(false);
    setCurrentConfigRecord(record);
    setConfigModalVisible(true);
    configForm.resetFields();

    // 加载项目空间下的业务场景列表
    await fetchDomainScenarios();

    // 解析当前用例已绑定的可见业务场景ID，并设置为默认选中
    const visibleBusinessIdStr = record.visibleBusinessId || '';
    const visibleIds = visibleBusinessIdStr
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim(), 10))
      .filter(id => !isNaN(id));

    if (visibleIds.length > 0) {
      configForm.setFieldsValue({ scenarioIds: visibleIds });
    }
  };

  // 配置可修改业务场景
  const handleConfigEditable = async (record) => {
    setConfigType('editable');
    setIsBatchConfig(false);
    setCurrentConfigRecord(record);
    setConfigModalVisible(true);
    configForm.resetFields();

    // 加载项目空间下的业务场景列表
    await fetchDomainScenarios();

    // 解析当前用例已绑定的可修改业务场景ID，并设置为默认选中
    const actionableBusinessIdStr = record.actionableBusinessId || '';
    const actionableIds = actionableBusinessIdStr
      .split(',')
      .filter(id => id.trim() !== '')
      .map(id => parseInt(id.trim(), 10))
      .filter(id => !isNaN(id));

    if (actionableIds.length > 0) {
      configForm.setFieldsValue({ scenarioIds: actionableIds });
    }
  };

  // 保存业务场景配置
  const handleConfigSave = async () => {
    try {
      const values = await configForm.validateFields();
      setConfigLoading(true);

      // 构建批量更新数据
      const caseIds = isBatchConfig ? selectedRowKeys : [currentConfigRecord.id];
      const data = {
        caseIds: caseIds,
      };

      // 根据配置类型设置对应的业务场景ID数组
      if (configType === 'visible') {
        data.visibleBusinessIds = values.scenarioIds || [];
      } else {
        data.actionableBusinessIds = values.scenarioIds || [];
      }

      // 调用批量更新接口
      await useCaseApi.batchUpdateBusinessScenarios(data);

      if (isBatchConfig) {
        message.success(`成功为 ${selectedRowKeys.length} 个用例配置${configType === 'visible' ? '可见' : '可修改'}业务场景`);
      } else {
        message.success(`配置${configType === 'visible' ? '可见' : '可修改'}业务场景成功`);
      }

      setConfigModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('配置业务场景失败:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();

    // 新增用例时，业务场景固定为当前进入的业务场景
    if (currentScenario) {
      form.setFieldsValue({
        source: '手工',
        scenarioId: currentScenario.id
      });
      // 根据当前场景筛选评测集
      const filtered = judgeSets.filter(set => set.businessScenarioId === currentScenario.id);
      setFilteredJudgeSets(filtered);
    } else {
      form.setFieldsValue({
        source: '手工'
      });
      // 重置评测集筛选
      setFilteredJudgeSets(judgeSets);
    }
  };

  // Excel导入用例（要素抽取场景专用）
  const handleExcelImport = () => {
    setExcelImportModalVisible(true);
    excelImportForm.resetFields();
    setExcelImportFile(null);
  };

  // 机审场景批量导入
  const handleAutoReviewImport = () => {
    setAutoReviewImportModalVisible(true);
    setAutoReviewImportFile(null);
    setAutoReviewImportPreview(null);
  };

  // 下载机审场景导入模板（后端生成）
  const handleDownloadAutoReviewTemplate = async () => {
    try {
      const response = await useCaseApi.downloadAutoReviewImportTemplate();
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '机审场景用例导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败，请重试');
    }
  };

  // 机审场景导入文件上传配置
  const autoReviewImportUploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    maxCount: 1,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return false;
      }
      setAutoReviewImportFile(file);
      return false;
    },
    onRemove: () => {
      setAutoReviewImportFile(null);
      setAutoReviewImportPreview(null);
    },
  };

  // 机审场景导入校验
  const handleAutoReviewImportPreview = async () => {
    if (!autoReviewImportFile) {
      message.error('请先选择要导入的Excel文件');
      return;
    }
    try {
      setAutoReviewImportUploading(true);
      const formData = new FormData();
      formData.append('file', autoReviewImportFile);
      const result = await useCaseApi.autoReviewImportPreview(formData);
      setAutoReviewImportPreview(result);
      // 根据校验结果显示不同提示
      if (result.invalidCount > 0) {
        message.error(`校验发现 ${result.invalidCount} 条无效数据，请查看错误信息`);
      } else {
        message.success('校验通过，可以导入');
      }
    } catch (error) {
      console.error('校验失败:', error);
      message.error('校验失败，请检查文件格式');
    } finally {
      setAutoReviewImportUploading(false);
    }
  };

  // 机审场景导入确认
  const handleAutoReviewImportConfirm = async () => {
    if (!autoReviewImportPreview || autoReviewImportPreview.validCount === 0) {
      message.error('没有有效的数据可以导入');
      return;
    }
    try {
      setAutoReviewImportUploading(true);
      const result = await useCaseApi.autoReviewImportConfirm(autoReviewImportPreview);
      if (result.failedCount === 0) {
        message.success(`成功导入 ${result.successCount} 条用例`);
        setAutoReviewImportModalVisible(false);
        setAutoReviewImportFile(null);
        setAutoReviewImportPreview(null);
        fetchData();
      } else {
        message.warning(`导入完成：成功 ${result.successCount} 条，失败 ${result.failedCount} 条`);
        setAutoReviewImportModalVisible(false);
        setAutoReviewImportFile(null);
        setAutoReviewImportPreview(null);
        fetchData();
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请重试');
    } finally {
      setAutoReviewImportUploading(false);
    }
  };

  // 异步导入任务状态轮询
  const [importTaskId, setImportTaskId] = useState(null);
  const [importStatus, setImportStatus] = useState(null); // 'pending' | 'running' | 'success' | 'failed'
  const [importProgress, setImportProgress] = useState({
    progress: 0,
    totalCount: 0,
    processedCount: 0,
    successCount: 0,
    failedCount: 0,
    message: '',
    failedDetails: [] // 失败详情列表
  });

  // 轮询导入状态
  const pollImportStatus = async (taskId) => {
    // 异步导入任务会一直执行直到完成，前端持续轮询直到任务结束
    // 不再设置最大轮询次数，避免误导性的"超时"提示
    let retryCount = 0;
    const maxRetries = 999999; // 理论上无限轮询，直到任务完成

    const poll = async () => {
      try {
        const result = await useCaseApi.getImportTaskStatus(taskId);

        // 获取失败详情（嵌套在 result.result.failedDetails 中）
        const failedDetails = result.result?.failedDetails || [];

        // 更新进度信息
        setImportProgress({
          progress: result.progress || 0,
          totalCount: result.totalCount || 0,
          processedCount: result.processedCount || 0,
          successCount: result.successCount || 0,
          failedCount: result.failedCount || 0,
          message: result.message || '',
          failedDetails: failedDetails
        });

        // 根据状态处理
        if (result.status === 'SUCCESS') {
          setImportStatus('success');
          const successMsg = result.failedCount > 0
            ? `用例导入完成！成功 ${result.successCount} 条，失败 ${result.failedCount} 条`
            : `用例导入完成！成功导入 ${result.successCount} 条数据`;
          
          // 如果有失败记录，不关闭弹窗，让用户查看失败详情
          if (failedDetails.length > 0) {
            message.warning(successMsg);
            setExcelImportUploading(false);
            setImportTaskId(null);
            fetchData();
          } else {
            message.success(successMsg);
            setExcelImportModalVisible(false);
            setExcelImportFile(null);
            setExcelImportUploading(false);
            setImportTaskId(null);
            fetchData();
          }
          return;
        } else if (result.status === 'FAILED') {
          setImportStatus('failed');
          message.error(`导入失败：${result.message || '未知错误'}`);
          setExcelImportUploading(false);
          setImportTaskId(null);
          return;
        } else if (result.status === 'RUNNING' || result.status === 'PENDING') {
          setImportStatus(result.status === 'RUNNING' ? 'running' : 'pending');
          retryCount++;
          // 异步任务会一直执行直到完成，前端持续轮询
          setTimeout(poll, 2000);
        } else {
          // 未知状态，继续轮询
          retryCount++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('查询导入状态失败:', error);
        retryCount++;
        // 查询失败时也继续重试，不轻易放弃
        setTimeout(poll, 2000);
      }
    };

    poll();
  };

  // Excel导入用例确认
  const handleExcelImportOk = async () => {
    try {
      if (!excelImportFile) {
        message.error('请先选择要导入的Excel文件');
        return;
      }

      if (!currentScenario) {
        message.error('请先进入业务场景');
        return;
      }

      setExcelImportUploading(true);
      setImportStatus('pending');
      setImportProgress({ progress: 0, totalCount: 0, processedCount: 0, successCount: 0, failedCount: 0, message: '', failedDetails: [] });

      const formData = new FormData();
      formData.append('file', excelImportFile);
      // 不再传递scenarioId，后端通过session或其他方式获取

      // 调用异步导入接口，返回任务ID
      const result = await useCaseApi.importExcel(formData);

      // 如果返回了任务ID，开始轮询状态
      if (result && result.taskId) {
        setImportTaskId(result.taskId);
        message.info('导入任务已提交，正在处理中...');
        // 开始轮询导入状态
        pollImportStatus(result.taskId);
      } else {
        // 兼容同步导入的情况（如果后端没有返回taskId，说明是同步完成的）
        message.success('用例导入成功');
        setExcelImportModalVisible(false);
        setExcelImportFile(null);
        setExcelImportUploading(false);
        fetchData();
      }
    } catch (error) {
      console.error('导入用例失败:', error);
      message.error('导入用例失败，请检查文件格式');
      setExcelImportUploading(false);
      setImportStatus(null);
    }
  };

  // Excel导入用例上传配置
  const excelImportUploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    maxCount: 1,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return false;
      }

      setExcelImportFile(file);
      return false;
    },
    onRemove: () => {
      setExcelImportFile(null);
    },
  };

  // 下载Excel导入模板
  const handleDownloadTemplate = async () => {
    try {
      // 调用后端接口下载Excel模板
      const response = await useCaseApi.downloadTemplate();

      // 创建Blob并下载
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '用例导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败，请重试');
    }
  };

  const handleEdit = async (record) => {
    setEditingRecord(record);
    setModalVisible(true);
    
    // 先根据业务场景筛选评测集，并等待状态更新
    if (record.scenarioId) {
      const filtered = judgeSets.filter(set => set.businessScenarioId === record.scenarioId);
      setFilteredJudgeSets(filtered);
    } else {
      setFilteredJudgeSets(judgeSets);
    }
    
    // 使用 setTimeout 确保状态更新后再设置表单值
    setTimeout(() => {
      form.setFieldsValue({
        caseName: record.caseName,
        scenarioId: record.scenarioId,
        judgeSetId: record.judgeSetId,
        documentId: record.documentId,
        source: record.source,
        tags: record.tags,
        businessExtInfo: record.businessExtInfo || '',
        expectedAnswer: record.expectedAnswer || '',
        resultInfo: record.resultInfo || '',
        hasUseModel: record.hasUseModel !== undefined ? record.hasUseModel : 'n',
        attachment: [],
        attachmentId: record.attachmentId || null
      });
    }, 0);
    
    // 处理附件信息 - 直接调用接口，不做任何判断
    try {
      const attachmentInfo = await useCaseApi.getAttachment(record.id);
      
      // 构建附件文件列表
      const attachmentFileList = [{
        uid: String(record.attachmentId || Date.now()),
        name: attachmentInfo.fileName || attachmentInfo.name || '附件文件',
        status: 'done',
        fileId: record.attachmentId,
        url: attachmentInfo.downloadUrl || attachmentInfo.url
      }];
      
      // 更新表单附件字段和文件类型字段
      form.setFieldsValue({
        attachment: attachmentFileList,
        attachmentType: attachmentInfo.attachmentType || ''
      });
    } catch (error) {
      // 静默处理错误，不显示任何提示
      console.error('获取附件信息失败:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await useCaseApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除用例失败:', error);
    }
  };

  const handleCopyHSFParams = () => {
    // 获取当前表单中的评测集ID和业务场景ID
    const scenarioId = form.getFieldValue('scenarioId');
    const judgeSetId = form.getFieldValue('judgeSetId');

    // 构建HSF参数JSON
    const hsfParams = [
      {
        businessExtInfo: "业务扩展信息",
        caseName: "用例名称",
        documentId: "单据唯一标识",
        expectedAnswer: "期望答案",
        judgeSetId: judgeSetId || "评测集id",
        scenarioId: scenarioId || "业务场景id",
        source: "手工",
        tags: ""
      }
    ];

    // 将JSON转换为字符串并复制到剪贴板
    const jsonString = JSON.stringify(hsfParams, null, 2);

    navigator.clipboard.writeText(jsonString).then(() => {
      message.success('HSF参数已复制到剪贴板');
    }).catch(err => {
      console.error('复制失败:', err);
      message.error('复制失败，请重试');
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // 获取附件信息
      let attachmentInfo = null;
      const attachmentList = values.attachment;

      // 判断是否为要素抽取或机审场景
      const isFeatureExtractionOrMachineAudit = currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW';

      if (attachmentList && Array.isArray(attachmentList) && attachmentList.length > 0) {
        const attachmentFile = attachmentList[0];
        attachmentInfo = {
          attachmentId: attachmentFile.fileId || values.attachmentId,
          attachmentName: attachmentFile.name,
          attachmentUrl: attachmentFile.url || '',
          attachmentType: values.attachmentType || ''
        };
      } else if (values.attachmentId) {
        // 编辑模式下，如果没有新上传附件但有原有attachmentId
        if (editingRecord && editingRecord.attachmentId) {
          attachmentInfo = {
            attachmentId: values.attachmentId,
            attachmentName: editingRecord.attachmentName || '',
            attachmentUrl: editingRecord.attachmentUrl || '',
            attachmentType: values.attachmentType || editingRecord.attachmentType || ''
          };
        }
      } else if (isFeatureExtractionOrMachineAudit && values.attachmentType) {
        // 要素抽取和机审场景：如果填写了文件类型但没有上传附件，也需要传attachment对象
        attachmentInfo = {
          attachmentId: null,
          attachmentName: '',
          attachmentUrl: '',
          attachmentType: values.attachmentType
        };
      }

      // 构建用例数据
      const useCaseData = {
        caseName: values.caseName,
        scenarioId: values.scenarioId,
        documentId: values.documentId || '',
        source: values.source,
        tags: values.tags || '',
        businessExtInfo: values.businessExtInfo || '',
        expectedAnswer: values.expectedAnswer || '',
        resultInfo: values.resultInfo || '',
        hasUseModel: values.hasUseModel !== undefined ? values.hasUseModel : 'n',
        judgeSetId: values.judgeSetId
      };

      // 构建提交数据（新格式）
      const submitData = {
        useCase: useCaseData,
        attachment: attachmentInfo
      };
      
      if (editingRecord) {
        await useCaseApi.update(editingRecord.id, submitData);
        message.success('修改成功');
      } else {
        await useCaseApi.create(submitData);
        message.success('新增成功');
      }
      
      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('保存用例失败:', error);
    }
  };

  const handleImport = () => {
    setImportModalVisible(true);
    importForm.resetFields();
    // 重置上传相关状态
    setUploadProgress({
      uploading: false,
      current: 0,
      total: 0,
      percent: 0
    });
    setIsUploading(false);
    setSelectedFiles([]);
    // 重置评测集筛选
    setFilteredJudgeSets(judgeSets);
  };

  const handleExportCases = () => {
    Modal.confirm({
      title: '确认导出',
      content: '确定要导出当前筛选条件下的用例吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 构建导出参数，筛选条件有scenarioId时使用条件中的，否则使用当前业务场景id
          const exportParams = {
            caseName: searchParams.caseName || undefined,
            scenarioId: searchParams.scenarioId || currentScenario?.id,
            source: searchParams.source || undefined,
            // 要素抽取和机审场景支持文件类型和标签参数
            attachmentType: (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? (searchParams.attachmentType || undefined) : undefined,
            tags: (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? (searchParams.tags || undefined) : undefined
          };
          
          // 调用导出接口
          const response = await useCaseApi.exportCases(exportParams);
          
          // 创建下载链接
          const blob = new Blob([response], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          
          // 生成文件名
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          link.download = `用例导出_${timestamp}.xlsx`;
          
          // 触发下载
          document.body.appendChild(link);
          link.click();
          
          // 清理
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          message.success('用例导出成功');
        } catch (error) {
          console.error('导出用例失败:', error);
          message.error('导出用例失败，请重试');
        }
      }
    });
  };

  const handleImportAnnotation = () => {
    setAnnotationModalVisible(true);
    annotationForm.resetFields();
    setAnnotationFile(null);
  };

  const handleAnnotationImport = async () => {
    try {
      if (!annotationFile) {
        message.error('请先选择要导入的Excel文件');
        return;
      }

      setAnnotationUploading(true);
      
      const formData = new FormData();
      formData.append('file', annotationFile);

      await annotationApi.batchUpdate(formData);
      
      message.success('标注结果导入成功');
      setAnnotationModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('导入标注结果失败:', error);
      message.error('导入标注结果失败，请检查文件格式');
    } finally {
      setAnnotationUploading(false);
    }
  };

  const annotationUploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    maxCount: 1,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return false;
      }
      
      setAnnotationFile(file);
      return false;
    },
    onRemove: () => {
      setAnnotationFile(null);
    },
  };

  const handleImportOk = async () => {
    try {
      const values = await importForm.validateFields();
      
      // 检查是否有上传结果
      if (!values.files || !values.files.uploadResults) {
        message.error('请先选择文件并等待上传完成');
        return;
      }
      
      const uploadResults = values.files.uploadResults;
      const successfulUploads = uploadResults.filter(result => result.success);
      
      if (successfulUploads.length === 0) {
        message.error('没有成功上传的文件可以导入');
        return;
      }
      
      // 构建批量导入数据
      const importData = {
        scenarioId: values.scenarioId,
        judgeSetId: values.judgeSetId,
        fileResults: successfulUploads
      };
      
      try {
        await useCaseApi.batchImport(importData);
        message.success(`成功导入 ${successfulUploads.length} 个用例`);
        setImportModalVisible(false);
        fetchData();
      } catch (importError) {
        message.error('批量导入失败，请重试');
        console.error('批量导入失败:', importError);
      }
    } catch (error) {
      console.error('导入用例失败:', error);
    }
  };


  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  const getSourceColor = (source) => {
    return source === '线上' ? 'blue' : 'green';
  };

  // 手动触发批量上传
  const handleStartUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0 || isUploading) {
      message.warning('请先选择要上传的文件');
      return;
    }

    // 设置上传中标记，防止重复调用
    setIsUploading(true);

    // 验证文件命名格式
    const invalidFiles = [];
    const validFiles = [];
    
    selectedFiles.forEach(file => {
      const fileName = file.name;
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));

       validFiles.push(file);

//       // 检查是否包含 - 分隔符并且格式正确
//       if (!nameWithoutExt.includes('-')) {
//         invalidFiles.push(fileName);
//       } else {
//         // 进一步验证格式：确保有用例名称和单据标识
//         const parts = nameWithoutExt.split('-');
//         if (parts.length < 2 || parts[0].trim() === '' || parts[1].trim() === '') {
//           invalidFiles.push(fileName);
//         } else {
//           validFiles.push(file);
//         }
//       }
    });
    
    // 显示验证结果
    if (invalidFiles.length > 0) {
      message.warning({
        content: (
          <div>
            <div>发现 {invalidFiles.length} 个文件命名不规范</div>
            <div className="text-xs text-gray-500 mt-1">
              上传时将被自动过滤，请检查文件命名
            </div>
          </div>
        ),
        duration: 3,
      });
    }
    
    if (validFiles.length === 0) {
      message.error('没有符合命名规范的文件可以上传');
      setIsUploading(false);
      return;
    }

    // 开始批量上传
    setUploadProgress({
      uploading: true,
      current: 0,
      total: validFiles.length,
      percent: 0
    });

    const uploadResults = [];
    let successCount = 0;
    let failCount = 0;

    try {
      // 逐个上传文件
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file.originFileObj || file);
          
          const uploadResponse = await useCaseApi.uploadFile(uploadFormData, currentScenario?.id);
          
          uploadResults.push({
            success: true,
            fileId: uploadResponse,
            fileName: file.name,
            message: '上传成功'
          });
          successCount++;
          
          // 上传成功后更新文件状态
          setSelectedFiles(prev => prev.map(f => 
            f.uid === file.uid ? { ...f, status: 'done' } : f
          ));
        } catch (error) {
          uploadResults.push({
            success: false,
            fileName: file.name,
            message: error.message || '上传失败'
          });
          failCount++;
        }

        // 更新进度
        const current = i + 1;
        const percent = Math.round((current / validFiles.length) * 100);
        setUploadProgress({
          uploading: true,
          current,
          total: validFiles.length,
          percent
        });
      }

      // 上传完成
      setUploadProgress({
        uploading: false,
        current: validFiles.length,
        total: validFiles.length,
        percent: 100
      });

      // 显示结果
      if (successCount > 0) {
        message.success(`批量上传完成：成功 ${successCount} 个，失败 ${failCount} 个`);
      }

      if (failCount > 0) {
        const failedFiles = uploadResults.filter(result => !result.success);
        message.warning({
          content: (
            <div>
              <div className="mb-2">部分文件上传失败：</div>
              <div className="text-red-600 text-sm">
                {failedFiles.map((result, index) => (
                  <div key={index}>• {result.fileName}: {result.message}</div>
                ))}
              </div>
            </div>
          ),
          duration: 6,
        });
      }

      // 保存上传结果到表单
      importForm.setFieldsValue({
        files: {
          uploadResults: uploadResults,
          fileList: selectedFiles
        }
      });

    } catch (error) {
      setUploadProgress({
        uploading: false,
        current: 0,
        total: 0,
        percent: 0
      });
      message.error('批量上传失败，请重试');
      console.error('批量上传失败:', error);
    } finally {
      // 重置上传标记
      setIsUploading(false);
    }
  };

  const batchUploadProps = {
    name: 'files',
    multiple: true,
    accept: '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv',
    fileList: selectedFiles, // 添加fileList属性控制显示
    beforeUpload: (file) => {
      // 检查文件大小（限制为10MB）
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(`文件 ${file.name} 大小不能超过10MB`);
        return false;
      }
      return false; // 阻止自动上传，完全手动控制
    },
    maxCount: 20,
    onChange: (info) => {
      // 限制文件数量
      if (info.fileList.length > 20) {
        message.warning('最多只能上传20个文件');
        info.fileList = info.fileList.slice(0, 20);
      }
      
      // 只更新文件列表，不触发上传
      setSelectedFiles(info.fileList);
      
      // 更新表单字段值
      importForm.setFieldsValue({
        files: {
          fileList: info.fileList
        }
      });
    },
  };

  // 用例附件上传配置
  const attachmentUploadProps = {
    name: 'attachment',
    multiple: false,
    accept: '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif',
    fileList: form.getFieldValue('attachment') || [],
    beforeUpload: (file) => {
      // 检查文件大小（限制为10MB）
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return false;
      }
      return false; // 阻止自动上传
    },
    maxCount: 1,
    onChange: async (info) => {
      // 处理附件选择并立即上传
      if (info.fileList.length > 0) {
        const file = info.fileList[0];
        
        // 检查文件状态
        if (file.status === 'removed') {
          form.setFieldsValue({ attachment: null });
          return;
        }
        
        // 立即上传文件
        try {
          setAttachmentUploading(true);
          const uploadFormData = new FormData();
          uploadFormData.append('file', file.originFileObj || file);
          
          const uploadResponse = await useCaseApi.uploadFile(uploadFormData, currentScenario?.id);
          
          // 上传成功，保存文件ID到表单字段
          const updatedFileList = [{
            uid: file.uid,
            name: file.name,
            status: 'done',
            fileId: uploadResponse // 保存文件ID
          }];
          
          // 同时设置attachment字段和attachmentId字段
          form.setFieldsValue({ 
            attachment: updatedFileList,
            attachmentId: uploadResponse // 直接设置attachmentId
          });
          
          message.success('附件上传成功');
        } catch (error) {
          setAttachmentUploading(false);
          // 上传失败
          const updatedFileList = [{
            uid: file.uid,
            name: file.name,
            status: 'error'
          }];
          
          form.setFieldsValue({ 
            attachment: updatedFileList,
            attachmentId: null // 清空attachmentId
          });
          message.error('附件上传失败，请重试');
        } finally {
          setAttachmentUploading(false);
        }
      } else {
        form.setFieldsValue({ 
          attachment: [],
          attachmentId: null // 清空attachmentId
        });
      }
    },
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用例名称',
      dataIndex: 'caseName',
      key: 'caseName',
      width: 200,
    },
    {
      title: '业务场景',
      dataIndex: 'scenarioId',
      key: 'scenarioName',
      width: 180,
      render: (scenarioId) => {
        const scenario = businessScenarios.find(s => s.id === scenarioId);
        const scenarioName = scenario ? scenario.scenarioName : '-';
        return (
          <Tooltip title={scenarioName} placement="topLeft">
            <Tag color="purple" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {scenarioName}
            </Tag>
          </Tooltip>
        );
      },
    },

    // 文件类型字段（要素抽取和机审场景显示）
    ...(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW' ? [
      {
        title: '文件类型',
        dataIndex: 'attachmentType',
        key: 'attachmentType',
        width: 250,
        render: (attachmentType) => attachmentType || '-',
      }
    ] : []),

    {
      title: '单据标识',
      dataIndex: 'documentId',
      key: 'documentId',
      width: 150,
      render: (documentId) => documentId || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      render: (source) => (
        <Tag color={getSourceColor(source)}>{source}</Tag>
      ),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: (tags) => (
        <div>
          {(tags || '').split(',').filter(tag => tag.trim()).map(tag => (
            <Tag key={tag} color="default" className="mb-1">{tag.trim()}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 230,
      fixed: 'right',
      render: (_, record) => {
        // 判断用例归属的业务场景是否是当前进入的业务场景
        const isOwnerScenario = record.scenarioId === currentScenario?.id;

        // 解析可修改业务场景ID列表（格式如 ",5," 或 ",5,6,"）
        const actionableBusinessIdStr = record.actionableBusinessId || '';
        const editableScenarioIds = actionableBusinessIdStr
          .split(',')
          .filter(id => id.trim() !== '')
          .map(id => parseInt(id.trim(), 10))
          .filter(id => !isNaN(id));

        // 判断当前业务场景是否在可修改业务场景列表中
        const isInEditableList = editableScenarioIds.includes(currentScenario?.id);

        // 如果用例归属的业务场景就是当前进入的业务场景，且有管理权限
        if (isOwnerScenario && hasManagePermission()) {
          // 构建下拉菜单项（只有要素抽取场景才有更多菜单）
          const menuItems = [];

          // 只有要素抽取场景才添加配置按钮（机审场景不显示）
          if (currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION') {
            menuItems.push(
              {
                key: 'configVisible',
                icon: <SettingOutlined />,
                label: '配置可见业务场景',
                onClick: () => handleConfigVisible(record),
              },
              {
                key: 'configEditable',
                icon: <SettingOutlined />,
                label: '配置可修改业务场景',
                onClick: () => handleConfigEditable(record),
              }
            );
          }

          return (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{
                  background: '#E5F5FF',
                  border: '1px solid #91D5FF',
                  color: '#1890FF'
                }}
              >
                编辑
              </Button>
              <Popconfirm
                title="确认删除"
                description="确定要删除这个用例吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  className="table-action-btn-delete"
                >
                  删除
                </Button>
              </Popconfirm>
              {menuItems.length > 0 && (
                <Dropdown
                  menu={{ items: menuItems }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button size="small" style={{
                    background: '#E5F5FF',
                    border: '1px solid #91D5FF',
                    color: '#1890FF'
                  }}>
                    更多 <DownOutlined />
                  </Button>
                </Dropdown>
              )}
            </Space>
          );
        }

        // 如果用例归属的业务场景不是当前进入的业务场景
        // 当前进入的业务场景在配置可修改业务场景列表中，且有管理权限，只显示编辑按钮
        if (isInEditableList && hasManagePermission()) {
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                style={{
                  background: '#E5F5FF',
                  border: '1px solid #91D5FF',
                  color: '#1890FF'
                }}
              >
                编辑
              </Button>
            </Space>
          );
        }

        // 当前进入的业务场景不在配置可修改业务场景列表中，不显示任何按钮
        return null;
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">用例管理</h1>
        <p className="page-description">管理评测用例，支持单个新增、批量导入和导出功能</p>
      </div>

      <div className="mb-4">
        <Space size="middle" className="mb-3">
          <Input.Search
            placeholder="请输入用例名称"
            allowClear
            size="large"
            className="w-80"
            onSearch={handleSearch}
            enterButton={<SearchOutlined />}
          />
          <Select
            placeholder="选择业务场景"
            allowClear
            size="large"
            className="w-48"
            onChange={handleScenarioFilter}
            loading={domainScenariosLoading}
          >
            {domainScenarios.map(scenario => (
              <Option key={scenario.value} value={scenario.value}>
                {scenario.text}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="选择来源"
            allowClear
            size="large"
            className="w-32"
            onChange={handleSourceFilter}
          >
            {sources.map(source => (
              <Option key={source} value={source}>
                {source}
              </Option>
            ))}
          </Select>
          {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
            <Select
              placeholder="文件类型"
              allowClear
              size="large"
              className="w-40"
              onChange={handleFileTypeFilter}
              loading={fileTypeLoading}
            >
              {fileTypeOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.text}
                </Option>
              ))}
            </Select>
          )}
          {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
            <Input.Search
              placeholder="请输入标签"
              allowClear
              size="large"
              className="w-48"
              onSearch={handleTagFilter}
              enterButton={<SearchOutlined />}
            />
          )}
        </Space>
        <div className="flex justify-end">
          <Space>
            {/* 要素抽取和机审场景：使用折叠菜单展示批量操作按钮 */}
            {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') ? (
              <>
                {/* 批量操作下拉菜单 */}
                {hasManagePermission() && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'batchBindJudgeSets',
                          icon: <AppstoreOutlined />,
                          label: '批量添加评测集',
                          disabled: selectedRowKeys.length === 0,
                          onClick: handleBatchBindJudgeSets,
                        },
                        // 机审场景不展示批量配置可见/可修改业务场景按钮
                        ...(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' ? [
                          {
                            key: 'batchConfigVisible',
                            icon: <SettingOutlined />,
                            label: '批量配置可见业务场景',
                            disabled: selectedRowKeys.length === 0,
                            onClick: handleBatchConfigVisible,
                          },
                          {
                            key: 'batchConfigEditable',
                            icon: <SettingOutlined />,
                            label: '批量配置可修改业务场景',
                            disabled: selectedRowKeys.length === 0,
                            onClick: handleBatchConfigEditable,
                          },
                        ] : []),
                        {
                          type: 'divider',
                        },
                        {
                          key: 'batchDelete',
                          icon: <DeleteOutlined />,
                          label: '批量删除',
                          disabled: selectedRowKeys.length === 0,
                          danger: true,
                          onClick: handleBatchDelete,
                        },
                      ],
                    }}
                    placement="bottomRight"
                  >
                    <Button
                      size="large"
                      style={{
                        background: '#1890FF',
                        borderColor: '#1890FF',
                        color: '#fff',
                      }}
                    >
                      批量操作 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`} <DownOutlined />
                    </Button>
                  </Dropdown>
                )}
                {hasManagePermission() && (
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    onClick={currentScenario?.scenarioCategory === 'AUTO_REVIEW' ? handleAutoReviewImport : handleExcelImport}
                    style={{
                      background: '#1890FF',
                      borderColor: '#1890FF',
                      color: '#fff',
                    }}
                  >
                    导入用例
                  </Button>
                )}
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={handleExportCases}
                  style={{
                    background: '#1890FF',
                    borderColor: '#1890FF',
                    color: '#fff',
                  }}
                >
                  导出用例
                </Button>
                {hasManagePermission() && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={handleAdd}
                    style={{
                      background: '#1890FF',
                      borderColor: '#1890FF',
                    }}
                  >
                    新增用例
                  </Button>
                )}
              </>
            ) : (
              <>
                {/* 非要素抽取场景：显示通用按钮 */}
                {hasManagePermission() && (
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    onClick={handleImport}
                  >
                    批量导入
                  </Button>
                )}
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={handleExportCases}
                  style={{
                    background: '#1890FF',
                    borderColor: '#1890FF',
                    color: '#fff',
                  }}
                >
                  导出用例
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  size="large"
                  onClick={handleImportAnnotation}
                >
                  导入标注结果
                </Button>
                {hasManagePermission() && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={handleAdd}
                    style={{
                      background: '#1890FF',
                      borderColor: '#1890FF',
                    }}
                  >
                    新增用例
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys) => {
              setSelectedRowKeys(selectedKeys);
            },
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </div>

      {/* 新增/编辑用例弹窗 */}
      <Modal
        title={editingRecord ? '编辑用例' : '新增用例'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnHidden
        okButtonProps={{
          disabled: attachmentUploading,
          loading: attachmentUploading
        }}
        cancelButtonProps={{
          disabled: attachmentUploading
        }}
      >
        <div className="flex justify-end mb-4">
          <Button
            type="primary"
            size="middle"
            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            onClick={() => handleCopyHSFParams()}
            icon={<CopyOutlined />}
          >
            复制HSF参数
          </Button>
        </div>
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="caseName"
            label="用例名称"
            rules={[
              { required: true, message: '请输入用例名称' },
              { max: 100, message: '用例名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入用例名称" />
          </Form.Item>
          
          <Form.Item
            name="scenarioId"
            label="业务场景"
            rules={[
              { required: true, message: '请选择业务场景' },
            ]}
          >
            <Select 
              placeholder="请选择业务场景"
              disabled={true}
              onChange={(value) => {
                filterJudgeSetsByScenario(value);
                form.setFieldsValue({ judgeSetId: undefined });
              }}
            >
              {businessScenarios.map(scenario => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.scenarioName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 要素抽取和机审场景：显示文件类型字段 */}
          {(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
            <Form.Item
              name="attachmentType"
              label="文件类型"
            >
              <Input placeholder="请输入文件类型，填写ALE中文件类型Code" />
            </Form.Item>
          )}
          
          {!(currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' || currentScenario?.scenarioCategory === 'AUTO_REVIEW') && (
            <Form.Item
              name="judgeSetId"
              label="评测集"
              rules={[
                { required: true, message: '请选择评测集' },
              ]}
            >
              <Select placeholder="请选择评测集">
                {filteredJudgeSets.map(set => (
                  <Option key={set.id} value={set.id}>
                    {set.setName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="documentId"
            label="单据唯一标识"
          >
            <Input placeholder="请输入单据唯一标识" />
          </Form.Item>
          
          <Form.Item
            name="source"
            label="来源"
            rules={[
              { required: true, message: '请选择来源' },
            ]}
          >
            <Select placeholder="请选择来源">
              {sources.map(source => (
                <Option key={source} value={source}>
                  {source}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="tags"
            label="标签"
            extra="多个标签用英文逗号分隔"
          >
            <Input placeholder="请输入标签，多个用逗号分隔" />
          </Form.Item>
          
          <Form.Item
            name="hasUseModel"
            label="是否使用模型评测"
            initialValue="n"
          >
            <Select placeholder="请选择是否使用模型评测">
              <Option value="n">否</Option>
              <Option value="y">是</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="businessExtInfo"
            label="业务扩展信息"
            extra="请输入有效的JSON格式数据"
          >
            <TextArea 
              rows={4} 
              placeholder='{"key1": "value1", "key2": "value2"}'
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
            />
          </Form.Item>
          
          <Form.Item
            name="expectedAnswer"
            label="期望答案"
            extra="请输入有效的JSON格式数据"
          >
            <TextArea 
              rows={4} 
              placeholder='{"result": "expected_result", "score": 0.95}'
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="resultInfo"
            label="执行结果"
            extra="请输入有效的JSON格式数据"
          >
            <TextArea 
              rows={4} 
              placeholder='{"result":"PASS"}'
              style={{ fontFamily: 'Monaco, Consolas, monospace' }}
            />
          </Form.Item>

          {/* 编辑时显示可见业务场景（只读），只有要素抽取场景显示（机审场景不显示） */}
          {editingRecord && currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
            <Form.Item
              label="可见业务场景"
            >
              <div style={{ padding: '4px 11px', background: '#f5f5f5', borderRadius: '6px', minHeight: '32px' }}>
                {(() => {
                  // 解析 visibleBusinessId 字符串格式（如 ",5,6,"）
                  const visibleBusinessIdStr = editingRecord.visibleBusinessId || '';
                  const visibleIds = visibleBusinessIdStr
                    .split(',')
                    .filter(id => id.trim() !== '')
                    .map(id => parseInt(id.trim(), 10))
                    .filter(id => !isNaN(id));

                  if (visibleIds.length === 0) {
                    return <span style={{ color: '#999' }}>暂无配置</span>;
                  }

                  // 根据ID获取业务场景名称
                  const visibleNames = visibleIds
                    .map(id => {
                      const scenario = businessScenarios.find(s => s.id === id);
                      return scenario ? scenario.scenarioName : null;
                    })
                    .filter(name => name !== null);

                  return visibleNames.length > 0 ? visibleNames.join('、') : <span style={{ color: '#999' }}>暂无配置</span>;
                })()}
              </div>
            </Form.Item>
          )}

          {/* 编辑时显示可修改业务场景（只读），只有要素抽取场景显示（机审场景不显示） */}
          {editingRecord && currentScenario?.scenarioCategory === 'FEATURE_EXTRACTION' && (
            <Form.Item
              label="可修改业务场景"
            >
              <div style={{ padding: '4px 11px', background: '#f5f5f5', borderRadius: '6px', minHeight: '32px' }}>
                {(() => {
                  // 解析 actionableBusinessId 字符串格式（如 ",5,6,"）
                  const actionableBusinessIdStr = editingRecord.actionableBusinessId || '';
                  const actionableIds = actionableBusinessIdStr
                    .split(',')
                    .filter(id => id.trim() !== '')
                    .map(id => parseInt(id.trim(), 10))
                    .filter(id => !isNaN(id));

                  if (actionableIds.length === 0) {
                    return <span style={{ color: '#999' }}>暂无配置</span>;
                  }

                  // 根据ID获取业务场景名称
                  const actionableNames = actionableIds
                    .map(id => {
                      const scenario = businessScenarios.find(s => s.id === id);
                      return scenario ? scenario.scenarioName : null;
                    })
                    .filter(name => name !== null);

                  return actionableNames.length > 0 ? actionableNames.join('、') : <span style={{ color: '#999' }}>暂无配置</span>;
                })()}
              </div>
            </Form.Item>
          )}

          <Form.Item
            name="attachment"
            label="附件上传"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
            extra="支持上传单个文件，格式：PDF、DOC、DOCX、TXT、JPG、PNG等，文件大小不超过10MB"
          >
            <Upload {...attachmentUploadProps}>
              <Button 
                icon={<UploadOutlined />}
                loading={attachmentUploading}
                disabled={attachmentUploading}
              >
                {attachmentUploading ? '上传中...' : '选择附件'}
              </Button>
            </Upload>
            {attachmentUploading && (
              <div className="mt-2 text-blue-600 text-sm">
                <span>📤 正在上传附件，请稍候...</span>
              </div>
            )}
          </Form.Item>
          
          {/* 隐藏字段用于存储attachmentId */}
          <Form.Item
            name="attachmentId"
            hidden
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入用例"
        open={importModalVisible}
        onOk={handleImportOk}
        onCancel={() => {
          setImportModalVisible(false);
          // 重置上传相关状态
          setUploadProgress({
            uploading: false,
            current: 0,
            total: 0,
            percent: 0
          });
          setIsUploading(false);
          setSelectedFiles([]);
        }}
        width={600}
        destroyOnHidden
      >
        <Form
          form={importForm}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="scenarioId"
            label="业务场景"
            rules={[
              { required: true, message: '请选择业务场景' },
            ]}
          >
            <Select 
              placeholder="请选择业务场景"
              onChange={(value) => {
                filterJudgeSetsByScenario(value);
                importForm.setFieldsValue({ judgeSetId: undefined });
              }}
            >
              {businessScenarios.map(scenario => (
                <Option key={scenario.id} value={scenario.id}>
                  {scenario.scenarioName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="judgeSetId"
            label="评测集"
            rules={[
              { required: true, message: '请选择评测集' },
            ]}
          >
            <Select placeholder="请选择评测集">
              {filteredJudgeSets.map(set => (
                <Option key={set.id} value={set.id}>
                  {set.setName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="files"
            label="选择文件"
            rules={[
              { 
                required: true, 
                message: '请选择要导入的文件',
                validator: (_, value) => {
                  if (!value || !value.fileList || value.fileList.length === 0) {
                    return Promise.reject(new Error('请选择要导入的文件'));
                  }
                  return Promise.resolve();
                }
              },
            ]}
          >
            <div>
              <Upload {...batchUploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  disabled={uploadProgress.uploading}
                >
                  选择文件（支持多选）
                </Button>
              </Upload>
              
              {selectedFiles.length > 0 && (
                <div className="mt-3">
                  <Button 
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleStartUpload}
                    disabled={uploadProgress.uploading}
                    className="mr-2"
                    style={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      borderColor: 'transparent',
                    }}
                  >
                    {uploadProgress.uploading ? '上传中...' : `开始上传 (${selectedFiles.length}个文件)`}
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedFiles([]);
                      importForm.setFieldsValue({
                        files: {
                          fileList: []
                        }
                      });
                    }}
                    disabled={uploadProgress.uploading}
                  >
                    清空文件
                  </Button>
                </div>
              )}
            </div>
            
            {/* 上传进度条 */}
            {uploadProgress.uploading && (
              <div className="mt-3 p-3 bg-blue-50 rounded border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    正在上传文件 ({uploadProgress.current}/{uploadProgress.total})
                  </span>
                  <span className="text-sm text-blue-600">
                    {uploadProgress.percent}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress.percent}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="mt-3 space-y-2">
              <div className="text-gray-600 text-sm font-medium">文件格式支持：</div>
              <div className="text-gray-500 text-sm">
                PDF、DOC、DOCX、TXT、JPG、PNG、GIF、XLSX、XLS、CSV
              </div>
              <div className="text-gray-600 text-sm font-medium">文件命名规范：</div>
              <div className="text-gray-500 text-sm">
                <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                  <div className="font-medium text-blue-800 mb-1">格式：用例名称-单据唯一标识.扩展名</div>
                  <div className="text-blue-700 text-xs">
                    示例：合同审查用例001-DOC20240115001.pdf
                  </div>
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded border-l-4 border-red-400 mb-2">
                <div className="text-red-800 text-sm font-medium">⚠️ 命名验证规则</div>
                <div className="text-red-700 text-xs mt-1">
                  • 文件名必须包含"-"分隔符<br/>
                  • 分隔符前后都必须有内容<br/>
                  • 不符合规范的文件将被拒绝导入
                </div>
              </div>
              <div className="text-orange-600 text-sm">
                • 一次最多可上传20个文件<br/>
                • 系统将逐个上传文件并显示进度<br/>
                • 实时验证文件命名格式
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 配置业务场景弹窗 */}
      <Modal
        title={
          isBatchConfig
            ? `批量配置${configType === 'visible' ? '可见' : '可修改'}业务场景`
            : `配置${configType === 'visible' ? '可见' : '可修改'}业务场景`
        }
        open={configModalVisible}
        onOk={handleConfigSave}
        onCancel={() => setConfigModalVisible(false)}
        width={600}
        destroyOnClose
        confirmLoading={configLoading}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={configForm}
          layout="vertical"
          className="mt-4"
        >
          {isBatchConfig && (
            <div className="mb-4 p-3 bg-blue-50 rounded">
              <div className="text-blue-800 text-sm">
                已选择 <strong>{selectedRowKeys.length}</strong> 个用例
              </div>
            </div>
          )}

          <Form.Item
            name="scenarioIds"
            label={`选择${configType === 'visible' ? '可见' : '可修改'}的业务场景`}
            rules={[
              { required: true, message: '请至少选择一个业务场景' },
            ]}
          >
            {domainScenariosLoading ? (
              <div className="text-center py-4 text-gray-500">加载中...</div>
            ) : domainScenarios.length === 0 ? (
              <div className="text-center py-4 text-gray-500">暂无可选的业务场景</div>
            ) : (
              <Checkbox.Group style={{ width: '100%' }}>
                <div className="space-y-2">
                  {domainScenarios.map(scenario => (
                    <div key={scenario.value} className="p-2 hover:bg-gray-50 rounded">
                      <Checkbox value={scenario.value}>
                        <span className="font-medium">{scenario.text}</span>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Checkbox.Group>
            )}
          </Form.Item>

          <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
            <div className="text-yellow-800 text-sm">
              <strong>说明：</strong>
              {configType === 'visible'
                ? '配置后，该用例将在选中的业务场景中可见'
                : '配置后，该用例在选中的业务场景中可以被修改'
              }
            </div>
          </div>
        </Form>
      </Modal>

      {/* Excel导入用例弹窗（要素抽取场景专用） */}
      <Modal
        title="导入用例"
        open={excelImportModalVisible}
        onOk={handleExcelImportOk}
        onCancel={() => {
          if (!excelImportUploading) {
            setExcelImportModalVisible(false);
            setExcelImportFile(null);
            setImportStatus(null);
            setImportProgress({ progress: 0, totalCount: 0, processedCount: 0, successCount: 0, failedCount: 0, message: '', failedDetails: [] });
          }
        }}
        width={700}
        destroyOnClose
        confirmLoading={excelImportUploading}
        okText={excelImportUploading ? '导入中...' : '开始导入'}
        cancelText="取消"
        okButtonProps={{ disabled: excelImportUploading }}
        cancelButtonProps={{ disabled: excelImportUploading }}
      >
        <div className="space-y-4">
          {/* 下载模板按钮 */}
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              style={{
                background: '#1890FF',
                borderColor: '#1890FF',
                boxShadow: 'none'
              }}
            >
              下载导入模板
            </Button>
          </div>

          <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-400">
            <div className="font-medium text-blue-800 mb-2">📋 Excel文件格式说明</div>
            <div className="text-blue-700 text-sm space-y-1">
              <div><strong>必填字段：</strong></div>
              <div className="ml-4">
                • 用例名称：用例的名称<br/>
                • 文件名称：附件文件名称<br/>
                • 文件类型：附件文件类型（ALE中配置的文件类型code）<br/>
                • 归属业务场景：用例所属的业务场景名称<br/>
                • url和文件key：两者必填一个
              </div>
              <div className="mt-2"><strong>可选字段：</strong></div>
              <div className="ml-4">
                • 业务ID：业务标识<br/>
                • 文件key：文件唯一标识（与url二选一必填）<br/>
                • url：文件访问地址、必须是可下载、无限制的文件地址（与文件key二选一必填）<br/>
                • 来源：用例来源（线上/手工）<br/>
                • 期望答案：JSON格式的期望答案<br/>
                • 执行结果：JSON格式的执行结果<br/>
                • 业务扩展信息：JSON格式的扩展信息<br/>
                • 可见业务场景：可见的业务场景名称，多个用英文逗号分隔<br/>
                • 可操作业务场景：可操作的业务场景名称，多个用英文逗号分隔
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-400">
            <div className="font-medium text-yellow-800 mb-2">⚠️ 重要提示</div>
            <div className="text-yellow-700 text-sm space-y-1">
              • 一次最多支持导入10000条数据<br/>
              • 请先下载模板，按照模板格式填写数据后再上传<br/>
              • 归属业务场景必须在系统中存在，否则该条数据将被跳过
            </div>
          </div>

          <Form form={excelImportForm} layout="vertical">
            <Form.Item
              label="选择Excel文件"
              required
            >
              <Upload {...excelImportUploadProps}>
                <Button
                  icon={<UploadOutlined />}
                  disabled={excelImportUploading}
                >
                  选择文件
                </Button>
              </Upload>
              {excelImportFile && !excelImportUploading && (
                <div className="mt-2 text-green-600 text-sm">
                  ✓ 已选择文件：{excelImportFile.name}
                </div>
              )}
            </Form.Item>
          </Form>

          {/* 异步导入进度显示 */}
          {excelImportUploading && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="font-medium text-blue-800">
                  {importStatus === 'pending' && '正在提交导入任务...'}
                  {importStatus === 'running' && '正在导入数据，请稍候...'}
                  {!importStatus && '正在处理...'}
                </span>
              </div>

              {/* 状态消息 */}
              {importProgress.message && (
                <div className="mb-3 text-sm text-blue-700">
                  {importProgress.message}
                </div>
              )}

              {/* 进度条 */}
              {(importProgress.progress > 0 || importProgress.totalCount > 0) && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-700">
                      导入进度：{importProgress.processedCount} / {importProgress.totalCount}
                    </span>
                    <span className="text-sm font-medium text-blue-800">
                      {importProgress.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* 成功/失败统计 */}
              {(importProgress.successCount > 0 || importProgress.failedCount > 0) && (
                <div className="mt-3 flex gap-4 text-sm">
                  <span className="text-green-600">
                    ✓ 成功：{importProgress.successCount}
                  </span>
                  {importProgress.failedCount > 0 && (
                    <span className="text-red-600">
                      ✗ 失败：{importProgress.failedCount}
                    </span>
                  )}
                </div>
              )}

              <div className="mt-3 text-xs text-blue-600">
                💡 导入过程中请勿关闭此窗口，系统正在后台处理数据...
              </div>
            </div>
          )}

          {/* 导入完成后的失败详情展示（当有失败记录且导入已完成时显示） */}
          {!excelImportUploading && importProgress.failedDetails && importProgress.failedDetails.length > 0 && (
            <div className="mt-4">
              {/* 导入结果概要 */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-yellow-600 text-lg">⚠️</span>
                  <span className="font-medium text-yellow-800">导入完成，部分用例导入失败</span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600">✓ 成功：{importProgress.successCount}</span>
                  <span className="text-red-600">✗ 失败：{importProgress.failedCount}</span>
                </div>
              </div>

              {/* 失败详情列表 */}
              <div className="p-3 bg-red-50 rounded border border-red-200 max-h-60 overflow-y-auto">
                <div className="font-medium text-red-800 mb-2">失败详情：</div>
                <div className="space-y-2">
                  {importProgress.failedDetails.map((detail, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border border-red-100 text-sm">
                      <div className="font-medium text-red-700 mb-1">
                        第 {detail.index} 行导入失败
                      </div>
                      <div className="text-gray-600 space-y-0.5 text-xs">
                        {detail.caseName && (
                          <div>用例名称：{detail.caseName}</div>
                        )}
                        {detail.extBusinessId && (
                          <div>业务ID：{detail.extBusinessId}</div>
                        )}
                        {detail.fileName && (
                          <div>文件名：{detail.fileName}</div>
                        )}
                        {detail.businessScenario && (
                          <div>归属业务场景：{detail.businessScenario}</div>
                        )}
                        <div className="text-red-600 font-medium mt-1">
                          失败原因：{detail.errorMessage || '未知错误'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 机审场景批量导入弹窗 */}
      <Modal
        title="导入用例"
        open={autoReviewImportModalVisible}
        onCancel={() => {
          if (!autoReviewImportUploading) {
            setAutoReviewImportModalVisible(false);
            setAutoReviewImportFile(null);
            setAutoReviewImportPreview(null);
          }
        }}
        width={900}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setAutoReviewImportModalVisible(false);
              setAutoReviewImportFile(null);
              setAutoReviewImportPreview(null);
            }}
            disabled={autoReviewImportUploading}
          >
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={autoReviewImportUploading}
            disabled={!autoReviewImportPreview || autoReviewImportPreview.validCount === 0}
            onClick={handleAutoReviewImportConfirm}
          >
            确认导入
          </Button>,
        ]}
      >
        <div className="space-y-4">
          {/* 下载模板按钮 */}
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadAutoReviewTemplate}
              style={{
                background: '#1890FF',
                borderColor: '#1890FF',
                boxShadow: 'none'
              }}
            >
              下载导入模板
            </Button>
          </div>

          {/* Excel文件格式说明 */}
          <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-400">
            <div className="font-medium text-blue-800 mb-2">📋 Excel文件格式说明</div>
            <div className="text-blue-700 text-sm space-y-1">
              <div><strong>必填字段：</strong></div>
              <div className="ml-4">
                • 用例名称、业务ID、业务场景、来源
              </div>
              <div className="mt-2"><strong>可选字段：</strong></div>
              <div className="ml-4">
                • 附件、机审结果（JSON格式）、预期答案（JSON格式）、业务扩展信息（JSON格式）
              </div>
            </div>
          </div>

          {/* 文件上传 */}
          <div className="bg-gray-50 p-4 rounded">
            <Upload {...autoReviewImportUploadProps}>
              <Button icon={<UploadOutlined />} disabled={autoReviewImportUploading}>
                选择 Excel 文件
              </Button>
            </Upload>
            {autoReviewImportFile && (
              <div className="mt-2 text-green-600 text-sm">
                ✓ 已选择文件：{autoReviewImportFile.name}
              </div>
            )}
          </div>

          {/* 开始校验按钮 */}
          {autoReviewImportFile && !autoReviewImportPreview && (
            <div className="flex justify-center">
              <Button
                type="primary"
                onClick={handleAutoReviewImportPreview}
                loading={autoReviewImportUploading}
              >
                开始校验
              </Button>
            </div>
          )}

          {/* 重要提示 - 始终显示在底部 */}
          <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-400">
            <div className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              重要提示
            </div>
            <div className="text-yellow-700 text-sm space-y-1">
              <div>• 单次最多支持导入 10,000 条数据</div>
              <div>• 业务场景必须是系统中已配置的场景名称</div>
              <div>• 来源字段只能填写：线上、手工</div>
              <div>• 预期答案和业务扩展信息必须是有效的 JSON 格式</div>
            </div>
          </div>

          {/* 校验结果 */}
          {autoReviewImportPreview && (
            <div className="space-y-4">
              {/* 校验通过提示 */}
              {autoReviewImportPreview.invalidCount === 0 ? (
                <div className="p-4 rounded border bg-green-50 border-green-200">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-xl">✓</span>
                    <span className="text-green-800 font-medium">
                      校验通过！共 {autoReviewImportPreview.totalCount} 条有效数据，可以导入。
                    </span>
                  </div>
                </div>
              ) : (
                /* 校验失败时展示错误列表 */
                <>
                  <div className="p-3 rounded border bg-red-50 border-red-200">
                    <div className="flex items-center gap-2">
                      <span className="text-red-600">✗</span>
                      <span className="text-red-800">
                        校验失败：共 {autoReviewImportPreview.totalCount} 条数据，
                        <span className="text-green-600 font-medium"> {autoReviewImportPreview.validCount} 条有效</span>，
                        <span className="text-red-600 font-medium"> {autoReviewImportPreview.invalidCount} 条无效</span>
                      </span>
                    </div>
                  </div>
                  {/* 仅展示错误数据列表 */}
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="border px-3 py-2 text-left">行号</th>
                          <th className="border px-3 py-2 text-left">用例名称</th>
                          <th className="border px-3 py-2 text-left">业务ID</th>
                          <th className="border px-3 py-2 text-left">错误信息</th>
                        </tr>
                      </thead>
                      <tbody>
                        {autoReviewImportPreview.rows?.filter(row => row.status === 'invalid').map((row) => (
                          <tr key={row.rowIndex} className="bg-red-50">
                            <td className="border px-3 py-2">{row.rowIndex}</td>
                            <td className="border px-3 py-2">{row.caseName || '-'}</td>
                            <td className="border px-3 py-2">{row.documentId || '-'}</td>
                            <td className="border px-3 py-2 text-red-600">{row.errorMessage || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* 批量添加评测集弹窗 */}
      <Modal
        title="批量添加评测集"
        open={batchBindJudgeSetsModalVisible}
        onOk={handleBatchBindJudgeSetsOk}
        onCancel={() => {
          setBatchBindJudgeSetsModalVisible(false);
          batchBindJudgeSetsForm.resetFields();
        }}
        width={600}
        destroyOnClose
        confirmLoading={batchBindJudgeSetsLoading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={batchBindJudgeSetsForm}
          layout="vertical"
          className="mt-4"
        >
          <div className="mb-4 p-3 bg-blue-50 rounded">
            <div className="text-blue-800 text-sm">
              已选择 <strong>{selectedRowKeys.length}</strong> 个用例
            </div>
          </div>

          <Form.Item
            name="setIds"
            label="选择评测集"
            rules={[
              { required: true, message: '请至少选择一个评测集' },
            ]}
          >
            {scenarioJudgeSets.length === 0 ? (
              <div className="text-center py-4 text-gray-500">当前业务场景下暂无评测集</div>
            ) : (
              <Checkbox.Group style={{ width: '100%' }}>
                <div className="space-y-2">
                  {scenarioJudgeSets.map(set => (
                    <div key={set.id} className="p-2 hover:bg-gray-50 rounded">
                      <Checkbox value={set.id}>
                        <span className="font-medium">{set.setName}</span>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Checkbox.Group>
            )}
          </Form.Item>

          <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
            <div className="text-yellow-800 text-sm">
              <strong>说明：</strong>
              选中的用例将被添加到所选评测集中
            </div>
          </div>
        </Form>
      </Modal>

      {/* 导入标注结果弹窗 */}
      <Modal
        title="导入标注结果"
        open={annotationModalVisible}
        onOk={handleAnnotationImport}
        onCancel={() => {
          setAnnotationModalVisible(false);
          setAnnotationFile(null);
        }}
        width={700}
        destroyOnClose
        confirmLoading={annotationUploading}
        okText="开始导入"
        cancelText="取消"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-400">
            <div className="font-medium text-blue-800 mb-2">📋 Excel文件格式说明</div>
            <div className="text-blue-700 text-sm space-y-1">
              <div><strong>必填字段：</strong></div>
              <div className="ml-4">
                • 用例ID：用于匹配系统中的用例<br/>
                • 期望答案：建议使用JSON格式，也可使用其他格式（需在后置脚本和断言脚本中自行处理）
              </div>
              <div className="mt-2"><strong>可选字段：</strong></div>
              <div className="ml-4">
                • 单据标识：单据唯一标识<br/>
                • 标签：用例标签，多个标签用英文逗号分隔<br/>
                • 业务扩展信息：建议使用JSON格式，也可使用其他格式（需在后置脚本和断言脚本中自行处理）
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-400">
            <div className="font-medium text-yellow-800 mb-2">⚠️ 重要提示</div>
            <div className="text-yellow-700 text-sm space-y-1">
              • 一次最多支持导入10000条数据<br/>
              • 期望答案和业务扩展信息的数据格式需要与后置脚本、断言脚本中的处理逻辑保持一致<br/>
              • 用例ID必须在系统中存在，否则该条数据将被跳过<br/>
              • 系统只识别上述字段，其他字段将被忽略
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <div className="font-medium text-gray-800 mb-3">示例数据格式：</div>
            <div className="bg-white p-3 rounded border text-xs font-mono overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-1">用例ID</th>
                    <th className="border px-2 py-1">期望答案</th>
                    <th className="border px-2 py-1">单据标识</th>
                    <th className="border px-2 py-1">标签</th>
                    <th className="border px-2 py-1">业务扩展信息</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-2 py-1">1001</td>
                    <td className="border px-2 py-1">{'{"result":"pass"}'}</td>
                    <td className="border px-2 py-1">DOC001</td>
                    <td className="border px-2 py-1">高优先级,核心</td>
                    <td className="border px-2 py-1">{'{"key":"value"}'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Form form={annotationForm} layout="vertical">
            <Form.Item
              label="选择Excel文件"
              required
            >
              <Upload {...annotationUploadProps}>
                <Button 
                  icon={<UploadOutlined />}
                  disabled={annotationUploading}
                >
                  选择文件
                </Button>
              </Upload>
              {annotationFile && (
                <div className="mt-2 text-green-600 text-sm">
                  ✓ 已选择文件：{annotationFile.name}
                </div>
              )}
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
}

export default UseCaseList;
